import { ASSETS } from "@/lib/assets";

/**
 * Cinematic homepage hero visual.
 *
 * The customer-supplied final hero render already contains the three architectural
 * layers (HSM / governance dashboard / biometric fingerprint / smart card) and the
 * "IDENTITY · HARDWARE · POLICY" caption baked in. We display it cleanly in a
 * square container with a subtle institutional frame — no overlays that would
 * compete with the image's existing caption.
 */
export const HeroArchitecture = () => (
  <div
    data-testid="hero-architecture"
    className="relative w-full aspect-square rounded-[28px] overflow-hidden border border-[#06102A]/10"
    style={{
      background:
        "radial-gradient(120% 80% at 75% 5%, #FFFFFF 0%, #F3F5F8 55%, #E4E7ED 100%)",
      boxShadow:
        "0 70px 140px -50px rgba(6,16,42,0.4), inset 0 1px 0 rgba(255,255,255,0.7)",
    }}
  >
    {/* Brushed sheen */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "linear-gradient(118deg, rgba(255,255,255,0.45) 0%, transparent 32%, rgba(255,255,255,0) 60%, rgba(6,16,42,0.04) 100%)",
      }}
    />

    {/* The final cinematic hero image — contained, never cropped */}
    <img
      src={ASSETS.heroFinal}
      alt="Vaultavo trust infrastructure — biometric smart card, MegaVault hardware, and governance platform"
      data-testid="hero-final-image"
      className="absolute inset-0 w-full h-full object-cover"
      style={{ objectPosition: "center" }}
    />

    {/* Soft vignette to merge edges into the white site background */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(120% 90% at 50% 50%, transparent 60%, rgba(255,255,255,0.30) 100%)",
      }}
    />

    {/* Top-left layer caption — the new hero render does not bake in a caption,
        so we add a subtle institutional one. */}
    <div className="absolute top-5 left-5 flex items-center gap-2 z-10">
      <span className="h-1.5 w-1.5 rounded-full bg-[#3B82F6] vt-pulse" />
      <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#06102A]/65 font-mono-vt">
        Identity · Hardware · Policy
      </p>
    </div>

    {/* Bottom-right credential block */}
    <div className="absolute bottom-5 right-5 text-right z-10">
      <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#06102A]/60 font-mono-vt">
        EAL6+
      </p>
    </div>
  </div>
);

export default HeroArchitecture;
